import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import MailjetHistorySection from '../../components/admin/MailjetHistorySection';
import {
  Send, Plus, Trash2, Edit2, Check, X, Paperclip,
  ChevronLeft, ChevronRight, Folder, RefreshCw, FileText,
  Users, AlertCircle, Eye, Calendar, Copy,
} from 'lucide-react';

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

function todayKst() {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

function formatDateKrFull(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const w = WEEKDAY_KR[new Date(y, m - 1, d).getDay()];
  return `${y}. ${String(m).padStart(2, '0')}. ${String(d).padStart(2, '0')}. (${w})`;
}

function formatDateKrShort(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const w = WEEKDAY_KR[new Date(y, m - 1, d).getDay()];
  return `${y}.${m}.${d} (${w})`;
}

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}


// ── 미리보기 모달 ─────────────────────────────────────────────────
function PreviewModal({ date, albumsPayload, onClose }) {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState('');
  const [originalHtml, setOriginalHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [error, setError] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.post('/api/notice-email/preview', { date, albums: albumsPayload })
      .then(res => {
        const token = localStorage.getItem('token') || '';
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const proxied = (res.data.html || '').replace(
          /https:\/\/drive\.google\.com\/uc\?export=download&id=([^"]+)/g,
          (_, fileId) => `${apiUrl}/api/notice-email/download-file/${fileId}?token=${encodeURIComponent(token)}`
        );
        setOriginalHtml(res.data.html || '');
        setHtml(proxied);
        setSubject(res.data.subject);
      })
      .catch(err => setError(err.response?.data?.message || '미리보기 생성 실패'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyHtml = async () => {
    try {
      if (navigator.clipboard?.write) {
        const blob = new Blob([originalHtml], { type: 'text/html' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
      } else {
        // HTTP 환경(비보안 컨텍스트) 폴백: contenteditable + execCommand
        const el = document.createElement('div');
        el.contentEditable = 'true';
        el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
        el.innerHTML = originalHtml;
        document.body.appendChild(el);
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      alert('클립보드 복사 실패: ' + e.message);
    }
  };

  const handleTestSend = async () => {
    if (!testEmail.trim()) { alert('이메일 주소를 입력해주세요.'); return; }
    setTestSending(true);
    try {
      await axios.post('/api/notice-email/test-send', { date, albums: albumsPayload, testEmail: testEmail.trim() });
      alert(`${testEmail}으로 테스트 발송 완료!`);
    } catch (e) {
      alert('테스트 발송 실패: ' + (e.response?.data?.message || e.message));
    } finally { setTestSending(false); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-[#111827] border border-white/[0.1] rounded-2xl overflow-hidden flex flex-col"
           style={{ width: '760px', maxWidth: '96vw', maxHeight: '90vh' }}>
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 mb-1">이메일 미리보기</p>
            <p className="text-white text-sm font-medium truncate">{subject}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors mt-0.5">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden" style={{ height: '60vh' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              <RefreshCw size={18} className="animate-spin mr-2" />미리보기 생성 중...
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full text-rose-400 text-sm">{error}</div>
          ) : (
            <iframe srcDoc={html} title="notice-preview" className="w-full bg-white" style={{ height: '60vh', border: 'none' }} />
          )}
        </div>
        <div className="px-5 py-4 border-t border-white/[0.06] flex items-center gap-2 shrink-0">
          <button
            onClick={handleCopyHtml}
            disabled={loading || !!error || !originalHtml}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-white/[0.12] text-slate-300 hover:text-white hover:border-white/[0.25] disabled:opacity-30 transition-colors shrink-0">
            {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
            {copied ? '복사됨' : '클립보드 복사'}
          </button>
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="테스트 발송할 이메일 주소"
            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50"
            onKeyDown={e => e.key === 'Enter' && handleTestSend()}
          />
          <button
            onClick={handleTestSend}
            disabled={testSending || !testEmail.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500/90 hover:bg-amber-500 text-white disabled:opacity-40 transition-colors shrink-0">
            {testSending ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
            테스트 발송
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Drive 파일 선택 모달 (2단계: 폴더선택 → 파일선택) ───────────
function DrivePickerModal({ albumId, date, initialFolderId, initialFolderName, initialSelected, onConfirm, onClose }) {
  const [step, setStep] = useState(initialFolderId ? 'files' : 'folders');
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(
    initialFolderId ? { folderId: initialFolderId, folderName: initialFolderName } : null
  );
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selected, setSelected] = useState(initialSelected || {});

  useEffect(() => {
    if (step === 'folders' && folders.length === 0) {
      setFoldersLoading(true);
      axios.get(`/api/notice-email/drive-folders?date=${date}`)
        .then(res => setFolders(res.data || []))
        .catch(() => setFolders([]))
        .finally(() => setFoldersLoading(false));
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 'files' && selectedFolder?.folderId) {
      setFilesLoading(true);
      axios.get(`/api/notice-email/drive-files/${selectedFolder.folderId}`)
        .then(res => setFiles(res.data || []))
        .catch(() => setFiles([]))
        .finally(() => setFilesLoading(false));
    }
  }, [step, selectedFolder?.folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id, name) => {
    setSelected(prev => prev[id]
      ? (({ [id]: _, ...rest }) => rest)(prev)
      : { ...prev, [id]: name }
    );
  };

  const [requestText, setRequestText] = useState({ generalRequest: '', melonRequest: '' });

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
    setFiles([]);
    setStep('files');
    // 요청사항 텍스트 병렬 로딩
    axios.get(`/api/notice-email/request-text/${folder.folderId}`)
      .then(res => setRequestText(res.data || {}))
      .catch(() => {});
  };

  const handleConfirm = () => {
    const ids = Object.keys(selected);
    const names = ids.map(id => selected[id]);
    onConfirm(albumId, selectedFolder?.folderId, selectedFolder?.folderName, ids, names, requestText);
  };

  const selCount = Object.keys(selected).length;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="bg-[#111827] border border-white/[0.1] rounded-2xl flex flex-col overflow-hidden"
           style={{ width: 520, maxWidth: '95vw', maxHeight: '80vh' }}>
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            {step === 'files' && selectedFolder && (
              <p className="text-xs text-slate-500 mb-0.5 flex items-center gap-1.5">
                <Folder size={12} className="text-amber-400" />
                {selectedFolder.folderName}
              </p>
            )}
            <p className="text-white font-semibold text-sm">
              {step === 'folders' ? 'Drive 폴더 선택' : '첨부파일 선택'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {step === 'folders' ? (
            foldersLoading ? (
              <div className="text-slate-500 text-sm text-center py-8 flex items-center justify-center gap-2">
                <RefreshCw size={14} className="animate-spin" />Drive 폴더 탐색 중...
              </div>
            ) : folders.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-8">해당 날짜의 폴더가 없습니다</div>
            ) : (
              <div className="space-y-1">
                {folders.map(f => (
                  <button key={f.folderId}
                    onClick={() => handleSelectFolder(f)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.06] text-left transition-colors">
                    <Folder size={14} className="text-amber-400 shrink-0" />
                    <span className="text-slate-300 text-sm">{f.folderName}</span>
                    <ChevronRight size={12} className="text-slate-600 ml-auto" />
                  </button>
                ))}
              </div>
            )
          ) : (
            filesLoading ? (
              <div className="text-slate-500 text-sm text-center py-8">불러오는 중...</div>
            ) : files.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-8">파일 없음</div>
            ) : (
              <div className="space-y-1">
                {files.map(f => (
                  <label key={f.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors">
                    <input
                      type="checkbox"
                      checked={!!selected[f.id]}
                      onChange={() => toggle(f.id, f.name)}
                      className="w-4 h-4 accent-rose-400"
                    />
                    <FileText size={14} className="text-slate-400 shrink-0" />
                    <span className="text-slate-300 text-sm">{f.name}</span>
                  </label>
                ))}
              </div>
            )
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step === 'files' && (
              <button onClick={() => setStep('folders')}
                className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors">
                <ChevronLeft size={12} />폴더 변경
              </button>
            )}
            {step === 'files' && selCount > 0 && (
              <span className="text-slate-500 text-sm">{selCount}개 선택됨</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-1.5 rounded-lg text-sm text-slate-400 hover:text-white border border-white/10 transition-colors">
              취소
            </button>
            {step === 'files' && (
              <button onClick={handleConfirm}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white transition-colors">
                확인 {selCount > 0 ? `(${selCount}개)` : '(파일 없이)'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 앨범 카드 ────────────────────────────────────────────────────
function AlbumNoticeCard({ album, form, onChange, onOpenPicker, onReloadRequestText }) {
  const [reloading, setReloading] = useState(false);

  const handleReload = async () => {
    if (!form.matchedFolderId) { alert('Drive 폴더를 먼저 선택해주세요.'); return; }
    setReloading(true);
    try {
      const res = await axios.get(`/api/notice-email/request-text/${form.matchedFolderId}`);
      onReloadRequestText(res.data || {});
    } catch (e) {
      alert('Drive 재조회 실패: ' + (e.response?.data?.message || e.message));
    } finally { setReloading(false); }
  };

  const removeFile = (idx) => {
    const newIds = (form.selectedFileIds || []).filter((_, i) => i !== idx);
    const newNames = (form.selectedFileNames || []).filter((_, i) => i !== idx);
    onChange({ selectedFileIds: newIds, selectedFileNames: newNames });
  };

  return (
    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
      <div className="flex gap-4 px-5 py-4 border-b border-white/[0.06]">
        {album.image ? (
          <img src={album.image} alt="cover"
            className="w-16 h-16 rounded-lg object-cover shrink-0 border border-white/10" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-white/[0.05] shrink-0 flex items-center justify-center">
            <FileText size={20} className="text-slate-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-base truncate">{album.nameKr}</p>
          <p className="text-slate-400 text-sm mt-0.5">{album.artist?.nameKr || ''}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-slate-500">
            {album.genre && <span>장르 <span className="text-slate-300">{album.genre}</span></span>}
            {album.albumType && <span>유형 <span className="text-slate-300">{album.albumType}</span></span>}
            {album.label && <span>기획사 <span className="text-slate-300">{album.label}</span></span>}
          </div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-400 shrink-0 w-28">티저/뮤직비디오</label>
          <input
            type="text"
            value={form.teaserMv || ''}
            onChange={e => onChange({ teaserMv: e.target.value })}
            placeholder="없음"
            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-1.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-slate-400">요청사항</label>
            {form.matchedFolderId && (
              <button onClick={handleReload} disabled={reloading}
                className="flex items-center gap-1 text-xs text-amber-400/80 hover:text-amber-300 disabled:opacity-50 transition-colors">
                <RefreshCw size={10} className={reloading ? 'animate-spin' : ''} />
                Drive 재조회
              </button>
            )}
          </div>
          <textarea
            value={form.generalRequest || ''}
            onChange={e => onChange({ generalRequest: e.target.value })}
            rows={8}
            placeholder="DB 등록 요청사항을 입력해주세요"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50 resize-y"
          />
        </div>

        <div>
          <label className="block text-xs text-slate-400 mb-1.5">
            멜론 전용 요청사항
            <span className="ml-2 text-slate-600">(비어있으면 일반 요청사항으로 발송)</span>
          </label>
          <textarea
            value={form.melonRequest || ''}
            onChange={e => onChange({ melonRequest: e.target.value })}
            rows={8}
            placeholder="멜론에만 다른 내용을 넣을 경우 입력"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50 resize-y"
          />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-xs text-slate-400">첨부파일</label>
            <button
              onClick={() => onOpenPicker(album.id)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-slate-300 bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.08] transition-colors">
              <Paperclip size={11} />
              Drive에서 선택
            </button>
          </div>
          {(form.selectedFileNames || []).length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {(form.selectedFileNames || []).map((name, i) => (
                <span key={i}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-500/10 border border-rose-500/20 rounded-full text-xs text-rose-300">
                  <FileText size={10} />
                  {name}
                  <button onClick={() => removeFile(i)} className="hover:text-rose-200 ml-0.5">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-600">선택된 파일 없음</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 수신자 관리 탭 ───────────────────────────────────────────────
function RecipientManager({ recipients, onReload }) {
  const [form, setForm] = useState({ platformName: '', email: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.platformName.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/notice-email/recipients', form);
      setForm({ platformName: '', email: '' });
      onReload();
    } catch (e) {
      alert('추가 실패: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await axios.put(`/api/notice-email/recipients/${id}`, editForm);
      setEditId(null);
      onReload();
    } catch (e) {
      alert('수정 실패');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" 수신자를 삭제할까요?`)) return;
    try {
      await axios.delete(`/api/notice-email/recipients/${id}`);
      onReload();
    } catch (e) { alert('삭제 실패'); }
  };

  const handleToggle = async (r) => {
    try {
      await axios.put(`/api/notice-email/recipients/${r.id}`, { isActive: !r.isActive });
      onReload();
    } catch (e) { alert('변경 실패'); }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditForm({ platformName: r.platformName, email: r.email, sortOrder: r.sortOrder });
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2">
          <Plus size={14} className="text-rose-400" />
          <span className="text-sm font-semibold text-white">수신자 추가</span>
        </div>
        <div className="px-5 py-4 flex gap-2">
          <input
            type="text"
            value={form.platformName}
            onChange={e => setForm(p => ({ ...p, platformName: e.target.value }))}
            placeholder="플랫폼명 (예: 멜론)"
            className="w-32 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50"
          />
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            placeholder="이메일"
            className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/50"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button onClick={handleAdd} disabled={saving || !form.platformName.trim() || !form.email.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white disabled:opacity-40 transition-colors shrink-0">
            추가
          </button>
        </div>
      </div>

      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2">
          <Users size={14} className="text-slate-400" />
          <span className="text-sm font-semibold text-white">수신자 목록</span>
          <span className="text-xs text-slate-500 ml-1">({recipients.length}명)</span>
        </div>
        {recipients.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-500 text-sm">수신자가 없습니다</div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {recipients.map(r => (
              <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                {editId === r.id ? (
                  <>
                    <input
                      value={editForm.platformName}
                      onChange={e => setEditForm(p => ({ ...p, platformName: e.target.value }))}
                      className="w-28 bg-white/[0.04] border border-white/[0.08] rounded px-2 py-1 text-xs text-white"
                    />
                    <input
                      value={editForm.email}
                      onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                      className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded px-2 py-1 text-xs text-white"
                    />
                    <input
                      type="number"
                      value={editForm.sortOrder}
                      onChange={e => setEditForm(p => ({ ...p, sortOrder: Number(e.target.value) }))}
                      placeholder="순서"
                      className="w-14 bg-white/[0.04] border border-white/[0.08] rounded px-2 py-1 text-xs text-white"
                    />
                    <button onClick={() => handleUpdate(r.id)} disabled={saving}
                      className="p-1.5 rounded text-emerald-400 hover:bg-emerald-400/10 transition-colors">
                      <Check size={14} />
                    </button>
                    <button onClick={() => setEditId(null)}
                      className="p-1.5 rounded text-slate-400 hover:bg-white/[0.06] transition-colors">
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleToggle(r)}
                      className={`w-2 h-2 rounded-full shrink-0 transition-colors ${r.isActive ? 'bg-emerald-400' : 'bg-slate-600'}`}
                      title={r.isActive ? '활성 (클릭하여 비활성화)' : '비활성 (클릭하여 활성화)'}
                    />
                    <span className="w-24 text-xs font-medium text-slate-300 shrink-0">{r.platformName}</span>
                    <span className="flex-1 text-xs text-slate-500 truncate">{r.email}</span>
                    <button onClick={() => startEdit(r)}
                      className="p-1.5 rounded text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-colors">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => handleDelete(r.id, r.platformName)}
                      className="p-1.5 rounded text-slate-600 hover:text-rose-400 hover:bg-rose-400/10 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 메인 페이지 ──────────────────────────────────────────────────
export default function AdminNoticeManagePage() {
  const [tab, setTab] = useState('send');
  const [date, setDate] = useState(todayKst());
  const [albums, setAlbums] = useState([]);
  const [albumForms, setAlbumForms] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [driveLoading, setDriveLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [driveModal, setDriveModal] = useState(null);

  // 발송 이력
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [statusData, setStatusData] = useState({});

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get('/api/notice-email/logs?limit=10');
      setLogs(res.data);
    } catch {}
    setLogsLoading(false);
  }, []);

  const fetchStatus = async (logId) => {
    setStatusData(prev => ({ ...prev, [logId]: { loading: true } }));
    try {
      const res = await axios.get(`/api/notice-email/logs/${logId}/status`);
      setStatusData(prev => ({ ...prev, [logId]: { data: res.data } }));
    } catch {
      setStatusData(prev => ({ ...prev, [logId]: { error: '조회 실패' } }));
    }
  };
  const [previewOpen, setPreviewOpen] = useState(false);
  const dateInputRef = useRef(null);
  const driveLoadedDatesRef = useRef(new Set());

  const loadRecipients = useCallback(() => {
    axios.get('/api/notice-email/recipients').then(res => setRecipients(res.data || []));
  }, []);

  useEffect(() => { loadRecipients(); }, [loadRecipients]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  function findDriveFolder(folders, artistName, albumName) {
    if (!folders.length) return null;
    const norm   = s => (s || '').toLowerCase().replace(/\s/g, '');
    const normKo = s => (s || '').replace(/[^가-힣]/g, ''); // 한글만 추출 (OST 등 수식어 차이 흡수)
    const bidirIncludes = (a, b) => a && b && (a.includes(b) || b.includes(a));

    // 1차: 아티스트명 매칭 (기존 동작)
    const name = norm(artistName);
    const byArtist = name
      ? folders.find(f => bidirIncludes(norm(f.artistName), name))
      : null;
    if (byArtist) return byArtist;

    // 2차: 앨범명 매칭 — OST/컴필(Various Artists)은 폴더가 작품명으로 만들어져 아티스트 매칭이 항상 실패
    // 예: 폴더 "불편한 편의점 OST" vs 앨범 "불편한 편의점 Original Soundtrack (OST)"
    const aName = norm(albumName);
    const byAlbum = aName
      ? folders.find(f => bidirIncludes(norm(f.artistName), aName))
      : null;
    if (byAlbum) return byAlbum;

    // 3차: 한글만 비교 (3자 이상일 때만 — 오탐 방지)
    const aKo = normKo(albumName);
    if (aKo.length >= 3) {
      return folders.find(f => {
        const fKo = normKo(f.artistName);
        return fKo.length >= 3 && bidirIncludes(fKo, aKo);
      }) || null;
    }
    return null;
  }

  const loadData = useCallback(async (targetDate) => {
    const d = targetDate || date;
    setLoading(true);
    setDriveLoading(false);
    setAlbums([]);
    setSent(false);

    let albumList = [];
    let draft = null;
    try {
      const [albumRes, draftRes] = await Promise.all([
        axios.get(`/api/notice-email/albums?date=${d}`),
        axios.get(`/api/notice-email/draft/${d}`).catch(() => ({ data: null })),
      ]);
      albumList = albumRes.data || [];
      draft = draftRes.data || null;
      setAlbums(albumList);

      setAlbumForms(prev => {
        const forms = {};
        albumList.forEach(album => {
          const empty = {
            generalRequest: '',
            melonRequest: '',
            teaserMv: '없음',
            selectedFileIds: [],
            selectedFileNames: [],
            matchedFolderId: null,
            matchedFolderName: null,
          };
          forms[album.id] = draft?.[album.id] || prev[album.id] || empty;
        });
        return forms;
      });
    } finally {
      setLoading(false);
    }

    // Drive 요청사항 비동기 로딩 — 드래프트에 Drive 데이터 없고 이 날짜에 처음 로드할 때만 실행
    // draft가 존재해도 matchedFolderId가 없으면 Drive를 아직 못 읽은 것 → 재시도
    const draftHasDriveData = draft && Object.values(draft).some(v => v?.matchedFolderId);
    if (albumList.length > 0 && !draftHasDriveData && !driveLoadedDatesRef.current.has(d)) {
      setDriveLoading(true);
      try {
        const driveRes = await axios.get(`/api/notice-email/drive-data?date=${d}`);
        const driveFolders = driveRes.data || [];
        const emptyForm = { generalRequest: '', melonRequest: '', teaserMv: '없음', selectedFileIds: [], selectedFileNames: [], matchedFolderId: null, matchedFolderName: null };

        // state callback 밖에서 직접 계산 — React 18 배치 타이밍 문제 회피
        const driveDataByAlbum = {};
        albumList.forEach(album => {
          const matched = findDriveFolder(driveFolders, album.artist?.nameKr || '', album.nameKr || '');
          driveDataByAlbum[album.id] = matched ? {
            ...emptyForm,
            generalRequest: matched.generalRequest || '',
            melonRequest:   matched.melonRequest   || '',
            matchedFolderId:   matched.folderId   || null,
            matchedFolderName: matched.folderName || null,
          } : { ...emptyForm };
        });

        setAlbumForms(prev => {
          const updated = { ...prev };
          albumList.forEach(album => {
            const m = driveDataByAlbum[album.id];
            if (m && updated[album.id]) {
              updated[album.id] = {
                ...updated[album.id],
                generalRequest: updated[album.id].generalRequest || m.generalRequest,
                melonRequest:   updated[album.id].melonRequest   || m.melonRequest,
                matchedFolderId:   updated[album.id].matchedFolderId   || m.matchedFolderId,
                matchedFolderName: updated[album.id].matchedFolderName || m.matchedFolderName,
              };
            }
          });
          return updated;
        });

        // driveDataByAlbum으로 DB 직접 저장 (state callback 의존 없음)
        if (Object.keys(driveDataByAlbum).length) {
          axios.put(`/api/notice-email/draft/${d}`, { data: driveDataByAlbum }).catch(() => {});
        }
        driveLoadedDatesRef.current.add(d);
      } catch (_) {
        // Drive 조회 실패 시 무시
      } finally {
        setDriveLoading(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive 데이터 강제 새로고침 — draftHasDriveData, ref 조건 모두 무시하고 직접 호출
  const reloadDrive = useCallback(async () => {
    const d = date;
    const currentAlbums = albums;
    if (!currentAlbums.length) return;
    setDriveLoading(true);
    try {
      const driveRes = await axios.get(`/api/notice-email/drive-data?date=${d}`);
      const driveFolders = driveRes.data || [];
      const emptyForm = { generalRequest: '', melonRequest: '', teaserMv: '없음', selectedFileIds: [], selectedFileNames: [], matchedFolderId: null, matchedFolderName: null };
      const driveDataByAlbum = {};
      currentAlbums.forEach(album => {
        const matched = findDriveFolder(driveFolders, album.artist?.nameKr || '', album.nameKr || '');
        driveDataByAlbum[album.id] = matched ? {
          ...emptyForm,
          generalRequest: matched.generalRequest || '',
          melonRequest:   matched.melonRequest   || '',
          matchedFolderId:   matched.folderId   || null,
          matchedFolderName: matched.folderName || null,
        } : { ...emptyForm };
      });
      setAlbumForms(prev => {
        const updated = { ...prev };
        currentAlbums.forEach(album => {
          const m = driveDataByAlbum[album.id];
          if (m && updated[album.id]) {
            updated[album.id] = {
              ...updated[album.id],
              generalRequest: m.generalRequest || updated[album.id].generalRequest,
              melonRequest:   m.melonRequest   || updated[album.id].melonRequest,
              matchedFolderId:   m.matchedFolderId   || updated[album.id].matchedFolderId,
              matchedFolderName: m.matchedFolderName || updated[album.id].matchedFolderName,
            };
          }
        });
        return updated;
      });
      if (Object.keys(driveDataByAlbum).length) {
        axios.put(`/api/notice-email/draft/${d}`, { data: driveDataByAlbum }).catch(() => {});
      }
      driveLoadedDatesRef.current.add(d);
    } catch (_) {
    } finally {
      setDriveLoading(false);
    }
  }, [date, albums]); // eslint-disable-line react-hooks/exhaustive-deps

  // 드래프트 자동저장 (debounce 1.5초)
  const saveTimerRef = useRef(null);
  const saveDraft = useCallback((currentDate, forms) => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      axios.put(`/api/notice-email/draft/${currentDate}`, { data: forms }).catch(() => {});
    }, 1500);
  }, []);

  const updateAlbumForm = (albumId, changes) => {
    setAlbumForms(prev => {
      const next = { ...prev, [albumId]: { ...prev[albumId], ...changes } };
      saveDraft(date, next);
      return next;
    });
  };

  const openPicker = (albumId) => {
    const form = albumForms[albumId] || {};
    const initialSelected = {};
    (form.selectedFileIds || []).forEach((id, i) => {
      initialSelected[id] = (form.selectedFileNames || [])[i] || id;
    });
    setDriveModal({
      albumId,
      initialFolderId: form.matchedFolderId || null,
      initialFolderName: form.matchedFolderName || null,
      initialSelected,
    });
  };

  const handlePickerConfirm = (albumId, folderId, folderName, ids, names, requestText = {}) => {
    const currentForm = albumForms[albumId] || {};
    updateAlbumForm(albumId, {
      selectedFileIds: ids,
      selectedFileNames: names,
      matchedFolderId: folderId || null,
      matchedFolderName: folderName || null,
      // 비어있을 때만 자동 채움
      generalRequest: currentForm.generalRequest || requestText.generalRequest || '',
      melonRequest:   currentForm.melonRequest   || requestText.melonRequest   || '',
    });
    setDriveModal(null);
  };

  const buildAlbumsPayload = () => albums.map(album => {
    const form = albumForms[album.id] || {};
    return {
      albumId: album.id,
      generalRequest: form.generalRequest || '',
      melonRequest: form.melonRequest || '',
      teaserMv: form.teaserMv || '없음',
      attachmentFileIds: form.selectedFileIds || [],
      attachmentFileNames: form.selectedFileNames || [],
    };
  });

  const handleSend = async () => {
    const activeCount = recipients.filter(r => r.isActive).length;
    if (!activeCount) { alert('활성 수신자가 없습니다. [수신자 관리] 탭에서 추가해주세요.'); return; }
    if (!albums.length) { alert('발매 앨범이 없습니다.'); return; }
    if (!window.confirm(`${formatDateKrShort(date)} 발매 공지를 ${activeCount}명에게 발송할까요?`)) return;
    setSending(true);
    try {
      const res = await axios.post('/api/notice-email/send', { date, albums: buildAlbumsPayload() });
      setSent(true);
      fetchLogs();
      alert(`발송 완료: ${res.data.sentCount}명 발송`);
    } catch (e) {
      alert('발송 실패: ' + (e.response?.data?.message || e.message));
    } finally { setSending(false); }
  };

  const activeRecipients = recipients.filter(r => r.isActive);

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">신규음원 공지 이메일</h1>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2 mb-4 flex-wrap">
        {[['send', Send, '공지 발송'], ['recipients', Users, '수신자 관리']].map(([key, Icon, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`appearance-none flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              tab === key
                ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
            }`}>
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* 공지 발송 탭 */}
      {tab === 'send' && (
        <div className="space-y-4">
          <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/[0.06] flex flex-wrap items-center gap-3">
              {/* 날짜 네비게이션 */}
              <div className="flex items-center gap-1">
                <button onClick={() => setDate(addDays(date, -1))}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <div className="relative">
                  <button
                    onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white text-sm font-medium hover:bg-white/[0.08] transition-colors">
                    {formatDateKrFull(date)}
                    <Calendar size={14} className="text-white" />
                  </button>
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                  />
                </div>
                <button onClick={() => setDate(addDays(date, 1))}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>

              <button onClick={() => loadData(date)} disabled={loading || driveLoading}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] text-slate-300 disabled:opacity-50 transition-colors">
                <RefreshCw size={14} className={(loading || driveLoading) ? 'animate-spin' : ''} />
                {loading ? '불러오는 중...' : driveLoading ? 'Drive 조회 중...' : '앨범정보 새로고침'}
              </button>
              <button onClick={reloadDrive} disabled={loading || driveLoading || !albums.length}
                title={!albums.length ? '발매 앨범이 없어 Drive를 조회할 수 없습니다' : 'Drive에서 요청사항 다시 불러오기'}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <RefreshCw size={14} className={driveLoading ? 'animate-spin' : ''} />
                Drive 새로고침
              </button>

              <div className="ml-auto flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${activeRecipients.length > 0 ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                <span className="text-xs text-slate-400">수신자 {activeRecipients.length}명</span>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {loading ? (
                <div className="text-center text-slate-500 text-sm py-8">
                  <RefreshCw size={24} className="mx-auto mb-3 animate-spin opacity-40" />
                  불러오는 중...
                </div>
              ) : albums.length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-10">
                  <AlertCircle size={32} className="mx-auto mb-3 opacity-30" />
                  {formatDateKrShort(date)} 발매 앨범이 없습니다
                </div>
              ) : (
                albums.map(album => (
                  <AlbumNoticeCard
                    key={album.id}
                    album={album}
                    form={albumForms[album.id] || {}}
                    onChange={changes => updateAlbumForm(album.id, changes)}
                    onOpenPicker={openPicker}
                    onReloadRequestText={({ generalRequest, melonRequest }) =>
                      updateAlbumForm(album.id, { generalRequest, melonRequest })
                    }
                  />
                ))
              )}
            </div>
          </div>

          {albums.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                앨범 {albums.length}건 · 수신자 {activeRecipients.length}명
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] text-slate-300 transition-colors">
                  <Eye size={15} />
                  미리보기
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || activeRecipients.length === 0}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
                    sent
                      ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                      : 'bg-rose-500 hover:bg-rose-600 text-white disabled:opacity-40'
                  }`}>
                  {sent ? <><Check size={15} />발송 완료</> : sending ? '발송 중...' : <><Send size={15} />공지 발송</>}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 수신자 관리 탭 */}
      {tab === 'recipients' && (
        <RecipientManager recipients={recipients} onReload={loadRecipients} />
      )}

      {/* Drive 파일 선택 모달 */}
      {/* 최근 발송 이력 */}
      <div className="mt-4">
        <NoticeEmailLogSection
          logs={logs}
          logsLoading={logsLoading}
          statusData={statusData}
          onRefresh={fetchLogs}
          onFetchStatus={fetchStatus}
        />
      </div>

      {/* 기간별 발송 조회 (Mailjet 직접) */}
      <div className="mt-4">
        <MailjetHistorySection
          baseUrl="/api/notice-email/mailjet-history"
          accent="sky"
        />
      </div>

      {driveModal && (
        <DrivePickerModal
          albumId={driveModal.albumId}
          date={date}
          initialFolderId={driveModal.initialFolderId}
          initialFolderName={driveModal.initialFolderName}
          initialSelected={driveModal.initialSelected}
          onConfirm={handlePickerConfirm}
          onClose={() => setDriveModal(null)}
        />
      )}

      {/* 미리보기 모달 */}
      {previewOpen && (
        <PreviewModal
          date={date}
          albumsPayload={buildAlbumsPayload()}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

function NoticeEmailLogSection({ logs, logsLoading, statusData, onRefresh, onFetchStatus }) {
  return (
    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-sm font-bold">최근 발송 이력</h3>
        <button
          onClick={onRefresh}
          className="text-xs text-slate-400 hover:text-white transition-colors"
        >
          새로고침
        </button>
      </div>

      {logsLoading ? (
        <p className="text-xs text-slate-500 text-center py-4">불러오는 중...</p>
      ) : logs.length === 0 ? (
        <p className="text-xs text-slate-600 text-center py-4">발송 이력이 없습니다.</p>
      ) : (
        <div className="space-y-3">
          {logs.map(log => {
            const sd = statusData[log.id];
            return (
              <div key={log.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-medium truncate">{log.subject}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {new Date(log.sentAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                      <span className="ml-2 text-slate-600">{log.recipientCount}명 발송</span>
                    </p>
                  </div>
                  <button
                    onClick={() => onFetchStatus(log.id)}
                    disabled={sd?.loading}
                    className="shrink-0 text-xs px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-colors disabled:opacity-50"
                  >
                    {sd?.loading ? '조회중...' : '상태 확인'}
                  </button>
                </div>

                {sd?.error && (
                  <p className="text-xs text-red-400 mt-2">{sd.error}</p>
                )}

                {sd?.data && (
                  <div className="mt-3 border-t border-white/[0.06] pt-3">
                    <div className="flex flex-wrap gap-4 mb-2 text-xs text-slate-500">
                      <span>열람 <span className="text-green-400 font-medium">{sd.data.results.filter(r => ['opened','clicked'].includes(r.status)).length}</span></span>
                      <span>미열람 <span className="text-slate-300 font-medium">{sd.data.results.filter(r => ['delivered','sent'].includes(r.status)).length}</span></span>
                      {sd.data.bounceCount > 0 && <span>반송 <span className="text-red-400 font-medium">{sd.data.bounceCount}</span></span>}
                      <span className="text-slate-600">전체 {sd.data.totalCount}명</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {sd.data.results.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded-lg bg-white/[0.02]">
                          <span className="text-slate-400 truncate mr-2">{r.platform || r.email}</span>
                          <span className={`shrink-0 font-medium ${r.color || 'text-gray-400'}`}>{r.label || r.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
